import { useState, useEffect, useCallback } from 'react';
import { useTestContext } from '../context/testContext';
import { calibrationConstants } from '../helpers/calibration-constants';
import { useCalibration } from '../context/calibrationContext';
import { cardDimensionCalculations, rulerDimensionCalculations } from '../helpers/calibration-calculations';
import { MainPanelContent } from './optician/main-panel-content';
import { useNavigationSync } from '../context/navigationContext';
import { ResponsiveLoadingPage } from './responsive-views';
import { useReceiveMessage } from '../context/messageContext';
import { NotesProvider } from '../context/notesContext';
import { SessionChromeProvider } from '../ui';
import { ClinicianSessionFrame } from './session/ClinicianSessionFrame';
import { useRestoreClinicianCalibration } from './session/calibration/sync';

const OpticianScreen = () => {
  const { setMultipleTestFieldValues, updateTestData } = useTestContext();
  const receivedMessage = useReceiveMessage();
  const [response, setResponse] = useState('');
  const { activeClinicianTab: activeTab, setActiveClinicianTab: setActiveTab } = useNavigationSync();
  const [calibrationStep, setCalibrationStep] = useState('calibration-selection');
  // Re-joining clinician: restore calibration / distance and the calibration sub-screen from the session state.
  useRestoreClinicianCalibration(setCalibrationStep);
  const [patientResponse, setPatientResponse] = useState({});
  const { tangent15degrees } = calibrationConstants;
  const {
    updateCalibrationData,
    calibrationData: { pixelDensity, blindSpotVanishingXDistancePx },
  } = useCalibration();

  useEffect(() => {
    if (activeTab !== 'test') updateTestData({ testInProgress: false, testKey: null });
  }, [updateTestData, activeTab]);

  const calculateDistanceFromScreen = useCallback(() => {
    const blindSpotVanishingXDistanceMm = blindSpotVanishingXDistancePx * pixelDensity;
    const distanceFromScreen = blindSpotVanishingXDistanceMm / tangent15degrees;

    updateCalibrationData({
      distanceFromScreen,
    });
  }, [blindSpotVanishingXDistancePx, pixelDensity, tangent15degrees, updateCalibrationData]);

  useEffect(() => {
    switch (receivedMessage?.type) {
      case 'cardDimensions':
        const cardDimensionsObject = receivedMessage?.content;
        updateCalibrationData(
          cardDimensionCalculations(
            cardDimensionsObject.cardHeightPx,
            cardDimensionsObject.cardWidthPx,
            cardDimensionsObject.screenWidthPx,
            cardDimensionsObject.screenHeightPx,
          ),
        );
        calculateDistanceFromScreen();

        if (cardDimensionsObject) {
          setResponse('Patient has entered card dimensions');
        }
        break;
      case 'rulerDimensions':
        const rulerDimensionsObject = receivedMessage?.content;

        updateCalibrationData(
          rulerDimensionCalculations(
            rulerDimensionsObject.rulerWidth,
            rulerDimensionsObject.rulerHeight,
            rulerDimensionsObject.screenWidthPx,
            rulerDimensionsObject.screenHeightPx,
          ),
        );
        setResponse('Patient has entered screen size');
        break;
      case 'changeLegacyViewToRulerDimensions':
        // Sent after a device selection (patient P3.1): C3.2 "<patient> has selected the <device>".
        setCalibrationStep('device');
        break;
      case 'cardCalibration':
        setCalibrationStep(receivedMessage?.content.screen);
        break;
      case 'calibration-selection':
        setCalibrationStep('calibration-selection');
        setResponse('');
        break;
      case 'screen-calibration':
        setCalibrationStep('screen-calibration');
        setResponse('');
        break;
      case 'camera-calibration':
        setCalibrationStep('camera-calibration');
        setResponse('');
        break;
      case 'blindspot-camera-calibration':
        setCalibrationStep('blindspot-camera-calibration');
        setResponse('');
        break;
      case 'patientMovementDetected':
        const data = {
          patientDistance: receivedMessage?.content.patientDistance,
          patientIsMoving: receivedMessage?.content.patientIsMoving,
        };
        setPatientResponse({ ...patientResponse, data });
        break;
      case 'blindSpotUpdate':
        setMultipleTestFieldValues(receivedMessage?.content);
        updateCalibrationData(receivedMessage?.content);
        break;
      case 'testCalibrationReset':
        setCalibrationStep('calibration-selection');
        break;
      default:
        break;
    }
  }, [receivedMessage]);

  return activeTab === 'welcome' ? (
    <ResponsiveLoadingPage />
  ) : (
    <NotesProvider>
      <SessionChromeProvider initialTitle='Loading…'>
        <ClinicianSessionFrame activeTab={activeTab} setActiveTab={setActiveTab}>
          <MainPanelContent
            calibrationStep={calibrationStep}
            setCalibrationStep={setCalibrationStep}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            response={response}
            setResponse={setResponse}
          />
        </ClinicianSessionFrame>
      </SessionChromeProvider>
    </NotesProvider>
  );
};

export default OpticianScreen;
